import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { AppShell, GqlProvider } from '@ah/design';
import { CalendarDays } from 'lucide-react';
import { useMe } from './useAuth';
import { gql, apiLogout } from './api';
import { clearSession } from './accounts';
import { Landing } from './routes/Landing';
import { LinkedInUrlGate } from './routes/LinkedInUrlGate';
import {
  GatheringsHome,
  GatheringDetail,
  GatheringLive,
  AttendingGatherings,
  PastGatherings,
  PostGathering,
} from '@ah/gatherings/desktop';
import { useTimezoneSync } from '@ah/people/shared';

// Wrapper component so we can call useTimezoneSync inside GqlProvider's
// context (useGql() reads from it). Effectively a no-op render — just a
// place to hang the side effect.
function PostAuthEffects({ enabled }: { enabled: boolean }) {
  // Push the browser timezone up once per session so server renderers
  // (email invites, WhatsApp pings) can format times in the user's zone.
  useTimezoneSync(enabled);
  return null;
}

// Mobile UAs that land on the desktop PWA (e.g. a calendar invite tapped
// on a phone *without* the native app installed — the OS has nothing to
// deep-link into, so it just opens the URL in the mobile browser) are
// forwarded to mobile.underlings.com with the same path + query. Cookies
// are scoped to .underlings.com so the auth state carries across.
//
// Skipped during native-app Capacitor runtime — the app itself sets
// `Capacitor` on window, and the deep-link bridge there already maps
// /gatherings/:id/live → /gatherings/:id.
function shouldForwardToMobileSubdomain(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as unknown as { Capacitor?: unknown }).Capacitor) return false;
  const ua = navigator.userAgent || '';
  // Match phone UAs only — tablets / desktops stay on the desktop PWA.
  return /iphone|ipod|android.*mobile|windows phone|blackberry|opera mini|iemobile/i.test(ua);
}

export function App() {
  const { data: me, isLoading } = useMe();
  const [params, setParams] = useSearchParams();
  const loc = useLocation();

  // Cross-subdomain forward — fires before any auth or routing work.
  useEffect(() => {
    if (!shouldForwardToMobileSubdomain()) return;
    const mobileBase = 'https://mobile.underlings.com';
    const next = `${mobileBase}${loc.pathname}${loc.search}${loc.hash}`;
    window.location.replace(next);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (params.get('auth_ok') || params.get('auth_error')) {
      const next = new URLSearchParams(params);
      next.delete('auth_ok');
      next.delete('auth_error');
      next.delete('need_linkedin_url');
      setParams(next, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoading) {
    return (
      <div className="min-h-dvh flex items-center justify-center text-sm text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!me) return <Landing />;
  if (params.get('need_linkedin_url')) return <LinkedInUrlGate me={me} />;

  // GatheringLive (countdown / live room / recap) is rendered full-screen
  // without the AppShell chrome.
  const inLive = /^\/gatherings\/[^/]+\/live\/?$/.test(loc.pathname);

  return (
    <GqlProvider client={gql}>
      <PostAuthEffects enabled={!!me} />
      {inLive ? (
        <Routes>
          <Route path="/gatherings/:id/live" element={<GatheringLive />} />
        </Routes>
      ) : (
        <AppShell
          user={me}
          brandIcon={
            <img
              src="https://assets.underlings.com/special/logo-mark.png"
              alt="Underlings"
              className="w-7 h-7 object-contain"
            />
          }
          links={[
            { to: '/gatherings', label: 'Gatherings', icon: <CalendarDays size={16} /> },
          ]}
          onLogout={async () => {
            await apiLogout();
            clearSession();
            window.location.href = '/';
          }}
        >
          <Routes>
            <Route path="/" element={<Navigate to="/gatherings" replace />} />
            <Route path="/gatherings" element={<GatheringsHome />} />
            <Route path="/gatherings/attending" element={<AttendingGatherings />} />
            <Route path="/gatherings/past" element={<PastGatherings />} />
            <Route path="/gatherings/:id" element={<GatheringDetail />} />
            <Route path="/gatherings/:id/recap" element={<PostGathering />} />
            <Route path="*" element={<Navigate to="/gatherings" replace />} />
          </Routes>
        </AppShell>
      )}
    </GqlProvider>
  );
}
